/** Values a message's `{name}` placeholders are filled with. */
export type MessageParams = Readonly<Record<string, string | number>>;

export type Translate<K extends string> = (
  key: K,
  params?: MessageParams,
) => string;

/**
 * Look a key up and fill its placeholders.
 *
 * A placeholder with no value is left as written: a visible `{name}` points
 * straight at the call that forgot it, where an empty string would hide it.
 */
export function createTranslator<K extends string>(
  messages: Readonly<Record<K, string>>,
): Translate<K> {
  return (key, params) =>
    messages[key].replace(/\{(\w+)\}/g, (placeholder, name: string) => {
      const value = params?.[name];
      return value === undefined ? placeholder : String(value);
    });
}

/** Keys that come as a `.one`/`.other` pair, named without the suffix. */
export type CountKey<K extends string> = PairedBase<K, K>;

// `Each` distributes over the keys; `All` stays whole so `.one` is looked up
// among every key rather than compared with the one `.other` key in hand.
type PairedBase<
  Each extends string,
  All extends string,
> = Each extends `${infer Base}.other`
  ? `${Base}.one` extends All
    ? Base
    : never
  : never;

export type Count<K extends string> = (
  key: CountKey<K>,
  count: number,
) => string;

/**
 * "1 item" / "2 items". The language's own plural rule picks the form —
 * Japanese has none, so it always lands on `.other` — and `{count}` is filled
 * in. Only `one` and `other` are kept: both languages need nothing more.
 */
export function createCounter<K extends string>(
  locale: string,
  t: Translate<K>,
): Count<K> {
  const rules = new Intl.PluralRules(locale);
  return (key, count) => {
    const form = rules.select(count) === "one" ? "one" : "other";
    return t(`${key}.${form}` as K, { count });
  };
}
